<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { noticeSendRefuse, readClassMsg } from "@/api/layout";
import { useMessageStore } from "@/stores/message";
import { useConfirm } from "@/composables/useFeedback";

const messageStore = useMessageStore();
const { alert } = useConfirm();

const classMsg = computed(() => messageStore.classMsg || {});
const dialogVisible = computed({
  get: () => !!classMsg.value.dialog,
  set: (v) => {
    if (!v) messageStore.closeClassMsg();
  },
});

const refuse = ref({ dialog: false, reason: "" });
const iframeRef = ref(null);

function setIframeContent() {
  nextTick(() => {
    try {
      const win = iframeRef.value?.contentWindow;
      if (win && typeof win.lodaTable === "function") {
        win.lodaTable(classMsg.value.content || "");
      }
    } catch (_) {
      /* ignore */
    }
  });
}

function onIframeLoad() {
  setIframeContent();
}

watch(
  () => classMsg.value.content,
  () => setIframeContent()
);

function doRefuse() {
  refuse.value = { dialog: true, reason: "" };
  messageStore.closeClassMsg(true);
}

function refuseCancel() {
  refuse.value.dialog = false;
  messageStore.openClassMsg();
}

async function refuseSubmit() {
  if (!refuse.value.reason.trim()) {
    await alert("pls give the reason why u Refuse", { type: "error" });
    return;
  }
  try {
    const res = await noticeSendRefuse(classMsg.value.mid, refuse.value.reason);
    messageStore.closeClassMsg();
    refuse.value.dialog = false;
    if (res?.data?.ErrorCode !== 0) {
      await alert(res?.data?.Msg || "Failed", { type: "error" });
    }
  } catch (_) {
    messageStore.closeClassMsg();
    refuse.value.dialog = false;
  }
}

async function readAndAccept() {
  try {
    const res = await readClassMsg(classMsg.value.mid);
    messageStore.closeClassMsg();
    if (res?.data?.ErrorCode !== 0) {
      await alert(res?.data?.Msg || "Failed", { type: "error" });
    }
  } catch (_) {
    messageStore.closeClassMsg();
  }
}
</script>

<template>
  <div>
    <el-dialog
      v-model="dialogVisible"
      align-center
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      class="notice-modal-dialog"
      width="60%"
    >
      <template #header>
        <div class="notice-title">{{ classMsg.title }}</div>
      </template>
      <iframe
        ref="iframeRef"
        name="iframeMap"
        class="notice-iframe"
        src="/static/noticeIframe.html"
        frameborder="0"
        @load="onIframeLoad"
      />
      <template #footer>
        <div class="notice-actions">
          <el-button type="danger" @click="doRefuse">Refuse</el-button>
          <el-button type="success" @click="readAndAccept">Read and Accept</el-button>
        </div>
      </template>
    </el-dialog>

    <el-dialog
      v-model="refuse.dialog"
      width="600px"
      align-center
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      title="give the reason here"
    >
      <el-input v-model="refuse.reason" type="textarea" :rows="5" />
      <template #footer>
        <div class="notice-actions">
          <el-button @click="refuseCancel">Cancle</el-button>
          <el-button type="primary" @click="refuseSubmit">Submit</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.notice-title {
  font-size: 16px;
  font-weight: 600;
  color: #32397a;
}

.notice-iframe {
  width: 100%;
  height: 500px;
  border: 0;
}

.notice-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>

<style lang="scss">
.notice-modal-dialog.el-dialog {
  min-width: 800px;
  max-width: 60%;
  word-break: break-word;

  @media screen and (max-width: 800px) {
    min-width: 100%;
    max-width: 100%;
  }
}
</style>
